import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { Text } from "react-native-paper";
import { theme } from "../theme/theme";
import Icon from "./Icon";

/**
 * Reusable HamburgerMenu component that provides:
 * 1. Hamburger button to toggle menu
 * 2. Overlay menu with customizable options
 * 3. Consistent styling across all screens
 *
 * @param {Array} menuOptions - Array of menu items with {id, title, icon, description}
 * @param {Function} onMenuItemPress - Callback when menu item is pressed (id) => void
 * @param {Object} buttonStyle - Optional custom styling for hamburger button
 * @param {number} iconSize - Size of hamburger icon (default: 30)
 * @param {string} iconColor - Color of hamburger icon (default: theme.colors.onPrimary)
 * @param {boolean} isVisible - External control for menu visibility
 * @param {Function} onClose - Callback when menu should close
 */
const HamburgerMenu = ({
  menuOptions = [],
  activeMenuId = null,
  onMenuItemPress,
  buttonStyle = {},
  iconSize = 30,
  iconColor = theme.colors.onPrimary,
  isVisible = false,
  onClose,
  position = "top", // 'top' or 'bottom'
}) => {
  const [showMenu, setShowMenu] = useState(false);

  // Use external state if provided, otherwise use internal state
  const isMenuVisible = isVisible !== false ? isVisible : showMenu;

  // Default menu options if none provided
  const defaultMenuOptions = [
    {
      id: "settings",
      title: "Settings",
      icon: "settings",
      description: "Account and app settings",
    },
    {
      id: "about",
      title: "About",
      icon: "home",
      description: "About Outback Exchange",
    },
    {
      id: "value-proposition",
      title: "Value Proposition",
      icon: "lightbulb",
      description: "Our mission and vision",
    },
    {
      id: "feedback",
      title: "Feedback",
      icon: "comment",
      description: "Help us improve the app",
    },
    {
      id: "sponsored-listings",
      title: "Offers, Deals & Bargains",
      icon: "star",
      description: "View sponsored offers",
    },
    {
      id: "sign-out",
      title: "Sign Out",
      icon: "logout",
      description: "Sign out of your account",
    },
  ];

  const options = menuOptions.length > 0 ? menuOptions : defaultMenuOptions;

  const handleMenuItemPress = (id) => {
    if (isVisible !== false) {
      // External state control
      if (onClose) onClose();
    } else {
      // Internal state control
      setShowMenu(false);
    }
    if (onMenuItemPress) {
      onMenuItemPress(id);
    }
  };

  const closeMenu = () => {
    if (isVisible !== false) {
      // External state control
      if (onClose) onClose();
    } else {
      // Internal state control
      setShowMenu(false);
    }
  };

  // Only render the menu overlay when visible
  return (
    <>
      {isMenuVisible && (
        <View style={styles.menuOverlay}>
          <TouchableOpacity
            style={styles.menuBackdrop}
            onPress={closeMenu}
            activeOpacity={1}
          />
          <View
            style={[
              styles.hamburgerMenu,
              position === "bottom" && styles.hamburgerMenuBottom,
            ]}
          >
            <View style={styles.hamburgerMenuOptions}>
              {options.map((option) => {
                const selected = option.id === activeMenuId && option.id !== "sign-out";
                return <View key={option.id}>
                  {option.id === "administration" ? <>
                    <View style={styles.menuDivider} />
                    <Text style={styles.sectionLabel}>Management</Text>
                  </> : null}
                  {option.id === "sign-out" ? <View style={styles.menuDivider} /> : null}
                  <TouchableOpacity
                    style={[styles.hamburgerMenuOption, selected && styles.activeOption]}
                    onPress={() => handleMenuItemPress(option.id)}
                    disabled={Boolean(option.disabled)}
                    accessibilityRole="button"
                    accessibilityLabel={`${option.title}${option.hiddenReminder ? ", private preview, hidden from other members" : ""}`}
                    accessibilityState={{ selected, disabled: Boolean(option.disabled) }}
                  >
                    <View style={styles.hamburgerMenuIcon}>
                      <Icon name={option.icon} size={20} color={selected ? theme.colors.primary : theme.colors.onSurface} />
                    </View>
                    <View style={styles.hamburgerMenuContent}>
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <Text style={[styles.hamburgerMenuTitle, selected && styles.activeTitle]}>{option.title}</Text>
                        {option.hiddenReminder ? <View style={styles.hiddenReminder}>
                          <Icon name="eye-off" size={14} color={theme.colors.onSurfaceVariant} />
                          <Text style={styles.hiddenReminderText}>Hidden</Text>
                        </View> : null}
                      </View>
                      {option.description ? <Text style={styles.hamburgerMenuDescription}>{option.description}</Text> : null}
                    </View>
                  </TouchableOpacity>
                </View>;
              })}
            </View>
          </View>
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  hamburgerButton: {
    padding: theme.spacing.sm,
  },
  menuOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99999,
    elevation: 99999,
  },
  menuBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.8)", // Darker backdrop
    zIndex: 99998,
  },
  hamburgerMenu: {
    position: "absolute",
    top: 140,
    left: theme.spacing.md,
    right: theme.spacing.md,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.spacing.md,
    elevation: 99999,
    zIndex: 99999,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  hamburgerMenuBottom: {
    top: "auto",
    bottom: theme.spacing.md,
    shadowOffset: {
      width: 0,
      height: -4, // Shadow points upward for bottom menu
    },
  },
  hamburgerMenuOptions: {
    paddingVertical: theme.spacing.sm,
  },
  hamburgerMenuOption: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.md,
  },
  hamburgerMenuIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.surfaceVariant,
    justifyContent: "center",
    alignItems: "center",
    marginRight: theme.spacing.md,
  },
  hamburgerMenuContent: {
    flex: 1,
  },
  hamburgerMenuTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: theme.colors.onSurface,
    marginBottom: 2,
    fontFamily: "Poppins",
  },
  hiddenReminder: {
    flexDirection: "row", alignItems: "center", gap: 4, paddingHorizontal: 6,
    paddingVertical: 3, borderWidth: 1, borderColor: theme.colors.outline, borderRadius: 6,
  },
  hiddenReminderText: { color: theme.colors.onSurfaceVariant, fontFamily: "Poppins", fontSize: 11 },
  sectionLabel: {
    color: theme.colors.onSurfaceVariant,
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: "500",
    paddingHorizontal: theme.spacing.md,
    paddingTop: 14,
    paddingBottom: 2,
  },
  activeOption: {
    backgroundColor: theme.colors.primaryContainer,
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.primary,
  },
  activeTitle: { color: theme.colors.primary },
  hamburgerMenuDescription: {
    fontSize: 17,
    color: theme.colors.onSurfaceVariant,
    fontFamily: "Poppins",
  },
  menuDivider: {
    height: 1,
    backgroundColor: theme.colors.outline,
    marginHorizontal: theme.spacing.md,
    opacity: 0.3,
  },
});

export default HamburgerMenu;
